/**
 * Lining a continuous daily dataset up with a chart that only has trading days.
 *
 * <p>The precomputed files carry every calendar day from 1801 to 2100. A chart carries the days the
 * exchange was open — roughly 252 in 365. So two different questions have to be answered, and they
 * are not inverses of each other:
 *
 * <ul>
 *   <li><b>Bar to value</b> (TrendPulse): every bar has a date, and a continuous daily file has an
 *       entry for every date. This is an exact lookup and needs no fallback — if it misses, the
 *       date is outside the dataset, which is a real answer.</li>
 *   <li><b>Event to bar</b> (CTS): an event can fall on a Saturday, a Sunday or a holiday, when no
 *       bar exists. It has to be shown SOMEWHERE or it is invisible, so it snaps to the nearest
 *       trading bar.</li>
 * </ul>
 */

/** The ISO day of a chart date, which may carry a time. */
function isoDay(value) {
  if (!value) return null;
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const MS_PER_DAY = 86400000;

/**
 * How far a snapped event may travel, in calendar days.
 *
 * <p>Four covers a weekend with a holiday on either side — the longest ordinary closure on the
 * exchanges here. Beyond that the "nearest" bar is not near, and drawing the event on it would be
 * asserting a relationship to a bar four days of price action away.
 */
import { foldCtsHits, CTS_COOLDOWN_BARS } from './ctsDeduplication';

export const MAX_SNAP_DAYS = 4;

/**
 * An index over a chart's dates, built once and reused for every lookup.
 *
 * <p>Built once because the alternative is quadratic: a scan per event over a 33-year chart with
 * hundreds of events is millions of comparisons, and it runs on the click.
 */
export function buildBarIndex(dates) {
  const list = Array.isArray(dates) ? dates : [];
  const byDate = new Map();
  const ordered = [];
  for (let i = 0; i < list.length; i++) {
    const day = isoDay(list[i]);
    if (!day) continue;
    // First wins. A duplicated date on the axis is a data defect, and pointing at the first of the
    // pair is at least stable between runs.
    if (!byDate.has(day)) byDate.set(day, i);
    ordered.push({ day, index: i, time: Date.parse(day) });
  }
  return { byDate, ordered, size: ordered.length };
}

/**
 * The bar for a date: the exact one, else the nearest trading bar.
 *
 * <h2>Ties go to the LATER bar</h2>
 * A Saturday is one day after Friday and two before Monday, so it lands on Friday. A Sunday is two
 * after Friday and one before Monday, so it lands on Monday. That is the convention the reader
 * expects, and it falls out of "nearest, ties later" rather than needing a weekday special case —
 * which matters because a holiday closure is not a weekend and has no weekday rule.
 *
 * <h2>It refuses rather than clamping</h2>
 * A date before the first bar or after the last returns -1. Clamping to the end bar is the failure
 * mode `DateMatcher.findClosestTradingDay` has on the backend: an event years past the chart snaps
 * to the final candle and reads as a finding about it. An event off the chart is not a finding.
 *
 * @returns {number} bar index, or -1
 */
export function alignToBar(index, date, { maxSnapDays = MAX_SNAP_DAYS } = {}) {
  const day = isoDay(date);
  if (!index || !day || index.size === 0) return -1;

  const exact = index.byDate.get(day);
  if (exact !== undefined) return exact;

  const time = Date.parse(day);
  if (Number.isNaN(time)) return -1;

  // Outside the chart entirely. Checked before the search so an off-chart date can never be
  // "nearest" to an edge bar.
  const first = index.ordered[0];
  const last = index.ordered[index.ordered.length - 1];
  if (time < first.time - maxSnapDays * MS_PER_DAY) return -1;
  if (time > last.time + maxSnapDays * MS_PER_DAY) return -1;

  // Binary search for the insertion point, then compare the two neighbours. The array is in chart
  // order, which for a price series is ascending by date.
  let lo = 0;
  let hi = index.ordered.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (index.ordered[mid].time < time) lo = mid + 1;
    else hi = mid - 1;
  }
  const after = index.ordered[lo];
  const before = index.ordered[lo - 1];

  const dAfter = after ? Math.round((after.time - time) / MS_PER_DAY) : Infinity;
  const dBefore = before ? Math.round((time - before.time) / MS_PER_DAY) : Infinity;

  // `<=` is what puts a tie on the later bar.
  const best = dAfter <= dBefore ? after : before;
  const distance = Math.min(dAfter, dBefore);
  if (!best || distance > maxSnapDays) return -1;
  return best.index;
}

/**
 * A value per chart bar, from a continuous daily series.
 *
 * <p>Exact lookups only, for the reason in the file header: every trading day is a calendar day, so
 * a miss means the bar is outside the dataset rather than that it fell on a weekend. Missing bars
 * come back as null so the chart can leave a gap — a study that interpolated across the gap would
 * be drawing values it does not have.
 *
 * @param {Array<{date: string, value: number}>} rows the dataset
 * @param {Array<string>} dates the chart's dates, in chart order
 * @returns {{values: Array<number|null>, matched: number, missing: number}}
 */
export function valuesForBars(rows, dates) {
  const byDate = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (r && typeof r.value === 'number' && Number.isFinite(r.value)) {
      byDate.set(isoDay(r.date), r.value);
    }
  });

  const values = [];
  let matched = 0;
  (Array.isArray(dates) ? dates : []).forEach((d) => {
    const v = byDate.get(isoDay(d));
    if (v === undefined) {
      values.push(null);
    } else {
      values.push(v);
      matched++;
    }
  });
  return { values, matched, missing: values.length - matched };
}

/**
 * The bars that carry a CTS reversal, from the flagged days in a dataset.
 *
 * <p>Several events can land on one bar — a Saturday and a Sunday both snap to the neighbouring
 * trading days, and a Friday-Saturday pair snaps to the same Friday. They are reported once, with
 * the dates that produced them, because two arrows on one bar would read as two independent
 * findings about it rather than as one weekend.
 *
 * @param {Array<{date: string, isReversal: boolean}>} rows
 * @param {{byDate: Map, ordered: Array}} index from buildBarIndex
 * @returns {{hits: Array<{barIndex:number, dates:string[], exact:boolean}>, flagged:number,
 *            snapped:number, offChart:number}}
 */
export function reversalBars(rows, index, opts = {}) {
  const byBar = new Map();
  let flagged = 0;
  let snapped = 0;
  let offChart = 0;

  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r || r.isReversal !== true) return;
    flagged++;
    const day = isoDay(r.date);
    const bar = alignToBar(index, day, opts);
    if (bar < 0) { offChart++; return; }

    const exact = index.byDate.get(day) === bar;
    if (!exact) snapped++;

    const existing = byBar.get(bar);
    if (existing) {
      existing.dates.push(day);
      // An exact hit on the bar outranks a snapped one for describing it: if any event fell ON this
      // bar, the bar's own date is the honest label.
      existing.exact = existing.exact || exact;
    } else {
      byBar.set(bar, { barIndex: bar, dates: [day], exact });
    }
  });

  const onBar = [...byBar.values()].sort((a, b) => a.barIndex - b.barIndex);

  // THE 5-BAR COOLDOWN, applied here because this is the first point at which a CTS date has
  // become a bar INDEX. Everything above merges days landing on the SAME bar; this folds runs of
  // signals on DIFFERENT bars that are too close to be separate events.
  //
  // That distinction is why the chart still showed clusters after the dataset was deduplicated:
  // three flagged days on three different bars produced three markers, and a calendar-day fold at
  // the read could not see bars at all.
  const windowBars = opts.cooldownBars === undefined ? CTS_COOLDOWN_BARS : opts.cooldownBars;
  const hits = foldCtsHits(onBar, windowBars);
  // ⚠ `onBar` IS RETURNED TOO, unfolded. The cluster glyph beside each mark has to answer "does
  // the NEXT bar carry a signal", and that question is about the bars the fold removed -- so the
  // caller needs the list, not just a count of how many went. Returning the count alone was the
  // reason this had to change.
  return { hits, onBar, flagged, snapped, offChart, folded: onBar.length - hits.length };
}
